import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Loader2, Mail, Lock } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1772657290984-b6f3f0da51e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjYXJpYmJlYW4lMjBsdXh1cnklMjB2aWxsYSUyMG9jZWFuJTIwdmlld3xlbnwwfHx8fDE3ODkyNzA3MDR8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      const ret = searchParams.get("return");
      navigate(ret || (u.role === "provider" ? "/provider" : u.role === "agent" ? "/agent" : "/dashboard"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060810] via-[#060810]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 on-dark">
          <h2 className="font-heading text-3xl font-bold text-white">Your next Caribbean escape<br />is one login away.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md fade-up">
          <div className="mb-8">
            <Logo size={40} tagline />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white">Welcome back</h1>
          <p className="text-slate-400 mt-2 mb-8 text-sm">Log in to manage your trips and bookings.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Email</label>
              <div className="flex items-center gap-3 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 focus-within:border-cyan-400/50">
                <Mail className="w-4 h-4 text-slate-500" />
                <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" className="bg-transparent text-white text-sm w-full outline-none" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Password</label>
              <div className="flex items-center gap-3 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 focus-within:border-cyan-400/50">
                <Lock className="w-4 h-4 text-slate-500" />
                <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="bg-transparent text-white text-sm w-full outline-none" />
              </div>
            </div>
            <button data-testid="login-submit-btn" disabled={loading}
              className="w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
            </button>
          </form>

          <p className="text-sm text-slate-400 text-center mt-6">
            New to Carivisor? <Link to="/register" className="text-cyan-400 hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
